import { connect } from 'react-redux';
import React from 'react';
import { Grid, Box } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import PixiRenderer from './PixiRenderer.js';
import PreviewElementsPipeline from './elements/pipelines/PreviewElementsPipeline';
import CanvasElementDragController from './interaction/CanvasElementDragController';
import CanvasElementHighlight from './interaction/CanvasElementHighlight';
import CanvasElementSnapGuides from './interaction/CanvasElementSnapGuides';
import ImageElementResizeController from './interaction/ImageElementResizeController';
import collectDragTargets from './interaction/collectDragTargets';
import { CONSTANTS, VISUALIZER_SHAPE, PREVIEW_RESOLUTION } from './PreviewDisplayConstants.js';
import isEmpty from 'lodash.isempty';
import { createActions } from '../store';
import { previewHistoryBatch } from '../store/extensions';
import { ASPECT_RATIO } from '../CreatePageConstants';
import { getMaxPositionForAspectRatio } from '../components/VideoElementsEditor/ImageElements/imageElementSizeCalculations';
import PreviewDisplayWarnings from './PreviewDisplayWarnings.jsx';
import WebGLContextLossAlertDialog from './WebGLContextLossAlertDialog.jsx';
import { trackEvent, AnalyticsConstants } from '../../../../utilities/analyticsUtilities';
import { isSafariBrowser } from '../../../../utilities/browserUtilities.js';
import { RealtimeAudioAnalyzer } from '../Audio/RealtimeAudioAnalyzer.js';
import { FpsCounter } from './FpsCounter.js';
import PreviewDisplayLoaders from './PreviewDisplayLoaders.jsx';

const styles = theme => ({
    container: {
        padding: theme.spacing(2),
        height: '100%'
    },
    gridContainer: {
        height: '100%'
    },
    canvasContainer: {
        width: '100%',
        maxHeight: '100%',
        position: 'relative',
        // An image dragged past the edge of the frame takes the outline that follows it with it, and
        // an absolutely positioned box sticking out of here would otherwise put a scrollbar under the
        // preview. Everything that belongs on top of the canvas is inset, so nothing else is clipped.
        overflow: 'hidden',
        // In fullscreen the browser sizes this box to the screen. The canvas inside keeps its own
        // aspect ratio (object-fit: contain), so the box only has to center it on black.
        '&:fullscreen, &:-webkit-full-screen': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxHeight: 'none',
            backgroundColor: 'black'
        }
    },
    watermarkImage: {
        position: 'absolute',
        zIndex: 1,
        bottom: 8,
        right: 4,
        maxWidth: '15%',
        opacity: 0.35,
        // The watermark covers the bottom right of the canvas, which is a corner users drop images
        // into. Letting pointer events through keeps those images grabbable.
        pointerEvents: 'none'
    }
});

const canvasElementStyles = `
    background: 'black';
    width: 100%;
    max-height: 100%;
    object-fit: contain;
`;
const canvasElementId = 'preview-display-canvas';

// The direct reference to the container of canvas element
const CanvasMountRef = React.createRef();

// The main PIXI Application for rendering
var PixiRendererInstance = null;

class PreviewDisplay extends React.Component {

    render() {
        const { hidden, classes } = this.props;
        
        return (
            <Box className={classes.container} hidden={hidden}>
                <Grid
                    container
                    direction='column' 
                    justifyContent='center' 
                    alignItems='center'
                    className={classes.gridContainer}
                >
                    <div ref={CanvasMountRef} className={classes.canvasContainer} id='preview-display-container'>
                        {/* Shows different loading messages and progress */}
                        <PreviewDisplayLoaders />                        
                        
                        {/* Shows different warnings */}
                        <PreviewDisplayWarnings />

                        <img className={classes.watermarkImage} src='https://specterr.b-cdn.net/specterr-preview-watermark.png' />
                    </div>
                </Grid>
                <WebGLContextLossAlertDialog />
            </Box>
        );
    }

    componentDidMount() {
        this.addRenderer(this);
        FpsCounter.assignHotkeys();
    }

    componentDidUpdate(prevProps) {
        const {
            appliedPlaybackTime: prevPlaybackTime,
            previewResolution: prevPreviewResolution,
            videoSettings: prevVideoSettings,
            playingPreview: prevPlayingPreview,
            optimizationModeEnabled: prevOptimizationMode
        } = prevProps;

        const { 
            appliedPlaybackTime, playingPreview, previewResolution, backgroundSettingsUpdateTimeStamp, videoSettings, 
            visualizerSettingsUpdateTimeStamp, textSettingsUpdateTimeStamp, visualizerLayersUpdateTimeStamp,
            lyricsSettingsUpdateTimeStamp, elementsSettingsUpdateTimeStamp, optimizationModeEnabled
        } = this.props;

        if (appliedPlaybackTime !== prevPlaybackTime) {
            this.startTime = performance.now();
            if (!playingPreview) this.animate();
        }

        if (prevPlayingPreview !== playingPreview) {
            if (videoSettings.visualizer.shape !== VISUALIZER_SHAPE.FLAT)
                (this.pipeline.getVisualizerRenderer()).resetSpinPosition();

            this.applyTickerSettings();
        }

        // If the resolution or aspect ratio changed, recreate the renderer first.
        // addRenderer() sets the correct CONSTANTS and applies all settings with the new dimensions,
        // so we can skip the per-setting updates below (they would use stale CONSTANTS otherwise).
        if (prevPreviewResolution !== previewResolution || prevVideoSettings.aspectRatio !== videoSettings.aspectRatio
            || prevOptimizationMode !== optimizationModeEnabled) {
            this.removeRenderer();
            this.addRenderer();
            this.refreshFrame(playingPreview);
            return;
        }

        if (!isEmpty(this.pipeline)) {
            const { setProperty } = this.props;
            const onLoadingStateChange = (isLoading) => {
                setProperty('webCodecsBufferLoading', isLoading);
            };
            const onPerformanceFallback = (unsupported) => {
                setProperty('videoAccelerationUnsupported', unsupported);
            };

            if (backgroundSettingsUpdateTimeStamp !== this.pipeline.backgroundSettingsUpdateTimeStamp) {
                this.pipeline.updateBackgroundSettings(this.buildPipelineConfig(onLoadingStateChange, onPerformanceFallback), backgroundSettingsUpdateTimeStamp);
                this.refreshFrame(playingPreview);
            }

            if (visualizerSettingsUpdateTimeStamp !== this.pipeline.visualizerSettingsUpdateTimeStamp) {
                this.pipeline.updateVisualizerSettings(videoSettings, visualizerSettingsUpdateTimeStamp, visualizerLayersUpdateTimeStamp);
                this.refreshFrame(playingPreview); 
            }

            if (textSettingsUpdateTimeStamp !== this.pipeline.textSettingsUpdateTimeStamp) {
                this.pipeline.updateTextSettings(videoSettings, textSettingsUpdateTimeStamp);
                this.refreshFrame(playingPreview);
            }

            if (lyricsSettingsUpdateTimeStamp !== this.pipeline.lyricsSettingsUpdateTimeStamp) {
                this.pipeline.updateLyricsSettings(videoSettings, lyricsSettingsUpdateTimeStamp);
                this.refreshFrame(playingPreview);
            }

            if (elementsSettingsUpdateTimeStamp !== this.pipeline.elementsSettingsUpdateTimeStamp) {
                this.pipeline.updateElementsSettings(videoSettings, elementsSettingsUpdateTimeStamp, () => this.refreshFrame(playingPreview));
                this.refreshFrame(playingPreview);
            } 
        }

        window.cancelAnimationFrame(this.animationRequest);

        if (playingPreview)
            this.animationRequest = window.requestAnimationFrame(() => this.animate(this));
    }

    componentWillUnmount() {
        const { resetVideoSettings } = this.props;
        
        resetVideoSettings();
        this.removeRenderer(this);

        FpsCounter.removeHotkeys();
        FpsCounter.disable();
    }

    addRenderer() {
        const {
            videoSettings, backgroundSettingsUpdateTimeStamp,
            visualizerSettingsUpdateTimeStamp, textSettingsUpdateTimeStamp, lyricsSettingsUpdateTimeStamp,
            appliedPlaybackTime, setProperty, playingPreview, elementsSettingsUpdateTimeStamp, optimizationModeEnabled
        } = this.props;

        // Optimization Mode pins the preview to the lowest resolution to reduce
        // memory usage; the user's stored preference is kept intact.
        const previewResolution = optimizationModeEnabled ? PREVIEW_RESOLUTION.TWO_FORTY : this.props.previewResolution;

        const onLoadingStateChange = (isLoading) => {
            setProperty('webCodecsBufferLoading', isLoading);
        };
        const onPerformanceFallback = (unsupported) => {
            setProperty('videoAccelerationUnsupported', unsupported);
        };

        onPerformanceFallback(false);
        onLoadingStateChange(false);

        // set the correct dimensions depending on the aspect ratio
        if (videoSettings.aspectRatio === ASPECT_RATIO.PORTRAIT) {
            CONSTANTS.DEFAULT_WIDTH = previewResolution;
            CONSTANTS.DEFAULT_HEIGHT = (previewResolution * 16) / 9;
            CONSTANTS.DEFAULT_MID_X = previewResolution / 2;
            CONSTANTS.DEFAULT_MID_Y = ((previewResolution * 16) / 9) / 2;
        } else if (videoSettings.aspectRatio === ASPECT_RATIO.SQUARE) {
            CONSTANTS.DEFAULT_WIDTH = previewResolution;
            CONSTANTS.DEFAULT_HEIGHT = previewResolution;
            CONSTANTS.DEFAULT_MID_X = previewResolution / 2;
            CONSTANTS.DEFAULT_MID_Y = previewResolution / 2;
        } else {
            CONSTANTS.DEFAULT_WIDTH = (previewResolution * 16) / 9;
            CONSTANTS.DEFAULT_HEIGHT = previewResolution;
            CONSTANTS.DEFAULT_MID_X = ((previewResolution * 16) / 9) / 2;
            CONSTANTS.DEFAULT_MID_Y = previewResolution / 2;
        }

        PixiRendererInstance = new PixiRenderer({ 
            width: CONSTANTS.DEFAULT_WIDTH, 
            height: CONSTANTS.DEFAULT_HEIGHT, 
            antialias: true 
        });

        // Dev-only handle used by the on-demand UI checks in SpecterrUiTests to walk the live
        // display tree (filter values, container bounds). Never exposed in a production build.
        if (process.env.NODE_ENV !== 'production') {
            window.__previewPixi = PixiRendererInstance;
            window.__previewPipeline = () => this.pipeline;
        }

        this.applyTickerSettings();

        this.startTime = performance.now();
        const canvas = PixiRendererInstance.getDomElement();
        canvas.style.filter = "drop-shadow(0px 4px 5px rgba(0,0,0,.75))";
        canvas.style.cssText = canvasElementStyles;
        canvas.setAttribute('aria-label', 'Video preview');
        canvas.setAttribute('id', canvasElementId);

        CanvasMountRef.current.appendChild(canvas);
        this.attachCanvasDragController(canvas);

        // It's a small fix for the webgl context that can be lost on the preview display.
        if (!isSafariBrowser()) {
            canvas.addEventListener('webglcontextlost', function (event) {
                event.preventDefault();

                const gl = canvas.getContext("webgl2");
                if (gl && !gl.isContextLost())
                    return;
                
                setProperty('webGLContextLossAlertDialogOpen', true);
                setProperty('playingPreview', false);
                trackEvent('Lost WebGL Context', {
                    category: AnalyticsConstants.VIDEO_CATEGORY,
                });
            }, false);
        }

        if (this.pipeline)
            this.pipeline.updateRenderer(PixiRendererInstance);
        else
            this.pipeline = new PreviewElementsPipeline(PixiRendererInstance, this.buildPipelineConfig(onLoadingStateChange, onPerformanceFallback));

        this.pipeline.updateBackgroundSettings(this.buildPipelineConfig(onLoadingStateChange, onPerformanceFallback), backgroundSettingsUpdateTimeStamp);
        this.pipeline.updateVisualizerSettings(videoSettings, visualizerSettingsUpdateTimeStamp);
        this.pipeline.updateTextSettings(videoSettings, textSettingsUpdateTimeStamp);
        this.pipeline.updateLyricsSettings(videoSettings, lyricsSettingsUpdateTimeStamp);
        this.pipeline.updateElementsSettings(videoSettings, elementsSettingsUpdateTimeStamp, () => this.refreshFrame(playingPreview));

        this.animationRequest = window.requestAnimationFrame(() => this.animate(this));
    }

    // Everything the pipeline is built and refreshed from, assembled in one place so that creating
    // the renderer and every later background update are fed exactly the same shape.
    buildPipelineConfig(onLoadingStateChange, onPerformanceFallback) {
        const { videoSettings, optimizationModeEnabled } = this.props;

        const previewResolution = optimizationModeEnabled ? PREVIEW_RESOLUTION.TWO_FORTY : this.props.previewResolution;
        
        const background = optimizationModeEnabled && videoSettings.background.videoAcceleration?.enabled
            ? { ...videoSettings.background, videoAcceleration: { ...videoSettings.background.videoAcceleration, enabled: false } }
            : videoSettings.background;

        return {
            ...videoSettings,
            background,
            previewResolution,
            onLoadingStateChange,
            onPerformanceFallback,
            backgroundVideoShouldPlay: this.backgroundVideoShouldPlay()
        };
    }

    // The one rule for the shared backdrop <video>, the same one BackgroundVideoElement applies:
    // it runs only while the preview does. ReactiveVideo has to be told, because it also touches that
    // element and would otherwise have to invent a rule of its own.
    backgroundVideoShouldPlay() {
        const { playingPreview, freezeVideoBackground } = this.props;

        return Boolean(playingPreview) && !freezeVideoBackground;
    }

    attachCanvasDragController(canvas) {
        this.destroyCanvasDragController();

        // Which store action a released gesture writes through. Assembled once: the drag controller
        // asks for its targets on every pointer event, and rebuilding these alongside them would
        // allocate three closures a frame for nothing.
        const commitGesture = {
            image: this.applyDraggedImageChange,
            text: this.applyDraggedTextChange,
            visualizer: this.applyDraggedVisualizerChange,
            lyrics: this.applyDraggedLyricsChange
        };

        this.canvasElementHighlight = new CanvasElementHighlight(CanvasMountRef.current, canvas);
        this.canvasElementSnapGuides = new CanvasElementSnapGuides(CanvasMountRef.current, canvas);
        this.canvasElementDragController = new CanvasElementDragController(canvas, {
            // Read per event rather than captured here: an element can be added, emptied or destroyed
            // between one pointer event and the next, and a list kept across that would hand the drag
            // something the renderer has already taken apart.
            getTargets: () => collectDragTargets(this.pipeline, this.props.videoSettings, commitGesture),
            // Read per drag rather than captured here: the aspect ratio can change while this
            // controller is alive, and a limit taken from the old frame would let the next drag
            // carry the element off the canvas.
            //
            // The frame decides the limit for every kind of element, not the sliders. It matches what
            // the image and text Position sliders offer, and it sits inside what the visualizer's
            // offer: those run to the widest frame's limits whatever shape is being edited, so taking
            // them literally would let a visualizer in a portrait frame be dragged off the canvas and
            // left somewhere the pointer can never reach it again.
            getMaxPosition: () => getMaxPositionForAspectRatio(this.props.videoSettings.aspectRatio),
            highlight: this.canvasElementHighlight,
            snapGuides: this.canvasElementSnapGuides
        });

        // The corner squares belong to the outline, so the resize reads which element it is working
        // on from there rather than hit testing the canvas again. It leaves everything but an image
        // element alone: text and the visualizer are dragged and never resized, and the outline draws
        // no squares around them.
        this.imageElementResizeController = new ImageElementResizeController(canvas, {
            highlight: this.canvasElementHighlight,
            getAspectRatio: () => this.props.videoSettings.aspectRatio,
            getMaxPosition: () => getMaxPositionForAspectRatio(this.props.videoSettings.aspectRatio)
        });
    }

    destroyCanvasDragController() {
        this.imageElementResizeController?.destroy();
        this.imageElementResizeController = null;

        this.canvasElementDragController?.destroy();
        this.canvasElementDragController = null;

        this.canvasElementHighlight?.destroy();
        this.canvasElementHighlight = null;

        this.canvasElementSnapGuides?.destroy();
        this.canvasElementSnapGuides = null;
    }

    // A canvas gesture hands back the picture's id rather than its index: the list can be reordered or
    // an element removed between the grab and the release, and the index would then point elsewhere.
    //
    // A corner resize scales and moves the sprite on every frame, because the corner opposite the one
    // being dragged stays put, so this carries a size as well as a position. Both land in one action,
    // and the whole gesture is a single undo step, exactly as a move drag is.
    applyDraggedImageChange = (imageElementId, properties) => {
        const { videoSettings, updateImageElement } = this.props;
        const imageElements = videoSettings.elements.imageElements || videoSettings.elements.images || [];
        const index = imageElements.findIndex(imageElement => imageElement.id === imageElementId);

        if (index !== -1)
            this.commitCanvasGesture(() => updateImageElement(index, properties));
    };

    // Text lines are addressed by index, which is what the Text panel and the store both use.
    applyDraggedTextChange = (index, properties) => {
        this.commitCanvasGesture(() => this.props.updateTextElement(index, properties));
    };

    // There is only ever one visualizer, and both of its shapes share the one position.
    applyDraggedVisualizerChange = (properties) => {
        this.commitCanvasGesture(() => this.props.updateVisualizerSettings(properties));
    };

    // Every line of lyrics shares the one position in the lyrics style, so a drag moves them all.
    applyDraggedLyricsChange = (properties) => {
        this.commitCanvasGesture(() => this.props.updateLyricsStyle(properties));
    };

    // A whole gesture is one action and therefore one undo step. Any grouping a settings panel left
    // open is closed first: a Position slider opens one when it is dragged and not every one of them
    // closes it again, and a canvas gesture joining a stale group would be undone along with it.
    commitCanvasGesture(dispatchUpdate) {
        previewHistoryBatch.end();
        dispatchUpdate();
    }

    removeRenderer() {
        this.destroyCanvasDragController();
        this.pipeline.destroy();
        this.pipeline = null;
        PixiRendererInstance.getDomElement().remove();
        PixiRendererInstance.app.destroy(true, {children: false, texture: false, baseTexture: false});
        PixiRendererInstance.app = null;
        PixiRendererInstance = null;
        window.cancelAnimationFrame(this.animationRequest);
    }

    applyTickerSettings = () => {
        const { optimizationModeEnabled, playingPreview } = this.props;
        if (!PixiRendererInstance)
            return;

        const ticker = PixiRendererInstance.app.ticker;
        ticker.maxFPS = optimizationModeEnabled ? 30 : 0;

        if (optimizationModeEnabled && !playingPreview)
            ticker.stop();
        else if (!ticker.started)
            ticker.start();
    }

    animate = () => {
        const { playingPreview, previewPositionDragging, appliedPlaybackTime, audioWaiting, freezeVideoBackground } = this.props;

        if (!(audioWaiting && playingPreview) || previewPositionDragging) {
            const calculatedTime = ((performance.now() - this.startTime) / 1000 + appliedPlaybackTime);
            const targetFrequencies = RealtimeAudioAnalyzer.getHistoricalFrequencies(playingPreview || previewPositionDragging);
            const currentBassFrequency = RealtimeAudioAnalyzer.getCurrentBassFrequency();
            // Scrubbing the playback position is not "playing", but the preview is still following
            // the user's hand and the backdrop should keep up with it. Idle editing is neither.
            const previewRunning = Boolean(playingPreview || previewPositionDragging);

            this.pipeline?.draw(targetFrequencies, currentBassFrequency, calculatedTime, playingPreview, freezeVideoBackground, previewRunning);

            if (PixiRendererInstance && !PixiRendererInstance.app.ticker.started)
                PixiRendererInstance.app.renderer.render(PixiRendererInstance.app.stage);

            // A line of lyrics can come or go under a pointer that is holding still, and only while the
            // preview is running does the frame change without a pointer event to settle the hover.
            if (previewRunning)
                this.canvasElementDragController?.refreshHover();
        }

        FpsCounter.triggerFrame();

        if (playingPreview)
            this.animationRequest = window.requestAnimationFrame(() => this.animate());
    }

    refreshFrame = (playingPreview) => {
        if (!playingPreview) {
            this.startTime = performance.now();
            this.animate();
        } 
    };
}

const mapStateToProps = (state) => {
    const {
        playingPreview,
        appliedPlaybackTime,
        audioWaiting,
        previewResolution,
        previewPositionDragging,
        freezeVideoBackground,
        optimizationModeEnabled,
    } = state.create;

    const {
        videoSettings,
        visualizerLayersUpdateTimeStamp,
        backgroundSettingsUpdateTimeStamp,
        visualizerSettingsUpdateTimeStamp,
        textSettingsUpdateTimeStamp,
        lyricsSettingsUpdateTimeStamp,
        elementsSettingsUpdateTimeStamp
    } = state.createPreview.present;

    return {
        videoSettings,
        playingPreview,
        appliedPlaybackTime,
        audioWaiting,
        previewResolution,
        visualizerLayersUpdateTimeStamp,
        backgroundSettingsUpdateTimeStamp,
        visualizerSettingsUpdateTimeStamp,
        textSettingsUpdateTimeStamp,
        lyricsSettingsUpdateTimeStamp,
        previewPositionDragging,
        elementsSettingsUpdateTimeStamp,
        // Optimization Mode implies frozen video backgrounds; the user's stored
        // preference is kept intact and applies again when the mode turns off.
        freezeVideoBackground: freezeVideoBackground || optimizationModeEnabled,
        optimizationModeEnabled
    }
};

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const resetVideoSettings = () => dispatch(createActions.resetVideoSettings());
    const updateImageElement = (index, properties) => dispatch(createActions.updateImageElement(index, properties));
    const updateTextElement = (index, properties) => dispatch(createActions.updateTextElement(index, properties));
    const updateVisualizerSettings = (properties) => dispatch(createActions.updateVisualizerSettings(properties));
    const updateLyricsStyle = (properties) => dispatch(createActions.updateLyricsStyle(properties));

    return {
        setProperty,
        resetVideoSettings,
        updateImageElement,
        updateTextElement,
        updateVisualizerSettings,
        updateLyricsStyle
    }
};

export { CanvasMountRef, PixiRendererInstance };
export default connect(mapStateToProps, mapDispatchToProps)(withStyles(PreviewDisplay, styles));